import { Tag } from "antd";
import { mapUnitToAcronym } from "./ouberUtils";
import { renderStatus } from "./generalUtils";

// Renders the chair-by-chair + dean status breakdown for one FSR
// submission's `all_approvals` array. Shared between the Approve Documents
// FSR queue and any other view that needs the same status presentation
// (e.g. a Dean's own oversight of submissions still under Program Chair
// review).
export const renderFsrStatus = (status, record) => {
  const allApprovals = record?.all_approvals || [];

  if (allApprovals.length === 0) {
    return renderStatus(["pending"], ["approved"], ["disapproved", "denied"])(status);
  }

  // Dean = approval with the highest id (always created last in storeFSR)
  const sorted = [...allApprovals].sort((a, b) => a.id - b.id);
  const deanApproval = sorted[sorted.length - 1];
  const chairApprovals = sorted.slice(0, -1);

  const getChairShortName = (chair) =>
    chair?.position?.position_name
      ?.replace(/^Program Chair,?\s*/i, "")
      .trim() ||
    chair?.position?.position_name ||
    "Chair";

  // Group chairs by status instead of listing one tag per chair, so the
  // cell stays at most 3 chair-tags tall no matter how many chairs there are.
  // "cancelled" chairs are ones whose turn never came because an earlier
  // chair already disapproved — distinct from still-WAITING ones now that
  // cancelled submissions are actually surfaced in these views.
  const chairGroups = { disapproved: [], pending: [], approved: [], cancelled: [] };
  chairApprovals.forEach((chair) => {
    const bucket = chairGroups[chair.status] ? chair.status : "pending";
    chairGroups[bucket].push(getChairShortName(chair));
  });

  const chairTagConfig = {
    disapproved: { color: "red", label: "DISAPPROVED" },
    pending: { color: "orange", label: "WAITING" },
    approved: { color: "green", label: "APPROVED" },
    cancelled: { color: "default", label: "CANCELLED" },
  };

  const chairTags = ["disapproved", "pending", "approved", "cancelled"]
    .filter((key) => chairGroups[key].length > 0)
    .map((key) => (
      <Tag color={chairTagConfig[key].color} key={key}>
        {chairTagConfig[key].label} - PROGRAM CHAIR ({chairGroups[key].join(", ")})
      </Tag>
    ));

  if (chairTags.length === 0) {
    chairTags.push(
      <Tag color="orange" key="no-chairs">
        WAITING - PROGRAM CHAIR
      </Tag>
    );
  }

  const allChairsApproved =
    chairApprovals.length > 0 &&
    chairGroups.disapproved.length === 0 &&
    chairGroups.pending.length === 0;

  const deanStatus = deanApproval?.status;
  const deanUnit = mapUnitToAcronym(deanApproval?.position?.unit);
  const deanLabel = deanUnit ? `DEAN (${deanUnit})` : "DEAN";

  const deanTagMap = {
    waiting: <Tag color="blue">WAITING - {deanLabel}</Tag>,
    pending: <Tag color="blue">WAITING - {deanLabel}</Tag>,
    approved: <Tag color="green">APPROVED - {deanLabel}</Tag>,
    disapproved: <Tag color="red">DISAPPROVED - {deanLabel}</Tag>,
  };

  const deanTag =
    allChairsApproved && deanStatus && deanStatus !== "cancelled"
      ? (deanTagMap[deanStatus] ?? null)
      : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      {chairTags}
      {deanTag}
    </div>
  );
};
